import { useState, useEffect, useRef, useCallback } from 'react'
import { Props_SnippetVideo } from './Video.props'
import { CompileHeadingToSpan, CompileMarkdown } from '../../../../Shared/AttraxTs/React/ReactSnippetMDCompiler'
import SnippetLink from '../Shared/Link/Link'
import VideoComponent from '../Shared/SharedVideo/SharedVideo'
import { setAutoplay, getVideoMimeType, getVideoType } from '../../../../Shared/AttraxTs/Generic/VideoStringHandler'

const SnippetVideo = (props: Props_SnippetVideo) => {
    const { heading, content, repeatable_link, video } = props
    const [isModalOpen, setIsModalOpen] = useState(false)
    const dialogRef = useRef<HTMLDialogElement>(null)

    const classHeadingVisibility = heading.visibility?.value ? '' : 'snippet-video__heading--hidden'
    const classContentAlignment = content.alignment?.value ? `snippet-video--content-alignment-${content.alignment.value}` : ''
    const classVideoFullscreen = video?.fullscreen?.value ? 'snippet-video--video-fullscreen' : ''
    const classVideoPosition = video?.position?.value ? `snippet-video--video-position-${video.position.value}` : ''

    const isModalMode = video?.position?.value === 'left' || video?.position?.value === 'right'

    const closeModal = useCallback(() => {
        setIsModalOpen(false)
    }, [])

    useEffect(() => {
        const dialog = dialogRef.current
        if (!dialog) return
        if (isModalOpen) {
            dialog.showModal()
            document.body.style.overflow = 'hidden'
        } else {
            dialog.close()
            document.body.style.overflow = ''
        }
        const handleClose = () => setIsModalOpen(false)
        dialog.addEventListener('close', handleClose)
        return () => {
            dialog.removeEventListener('close', handleClose)
            document.body.style.overflow = ''
        }
    }, [isModalOpen])

    const renderModalVideo = () => {
        const url = video?.url.value ?? ''
        const videoType = getVideoType(url)

        if (videoType === 'video') {
            return (
                <video
                    className="snippet-video__modal-video"
                    width={1280}
                    height={720}
                    controls
                    controlsList="nodownload"
                    autoPlay
                >
                    <track kind="captions" />
                    <source src={url} type={getVideoMimeType(url)} />
                </video>
            )
        }

        return (
            <iframe
                className="snippet-video__modal-video"
                width={1280}
                height={720}
                src={setAutoplay(url)}
                title={`{{js.snippet.video.iframe.title}}`}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                referrerPolicy={videoType === 'youtube' ? 'strict-origin-when-cross-origin' : undefined}
                allowFullScreen
            />
        )
    }

    return (
        <section className={`snippet-video ${classVideoFullscreen} ${classVideoPosition} ${classContentAlignment}`}>
            <div className="snippet-video__container">
                <div className="snippet-video__container-content">
                    <h2 className={`snippet-video__heading ${classHeadingVisibility}`}>{CompileHeadingToSpan(heading.content.value!)}</h2>
                    <div className="snippet-video__content">
                        {content.content?.value && CompileMarkdown(content?.content?.value)}
                    </div>
                    <div className="snippet-video__container-links">
                        {repeatable_link?.map(({ linkHref, linkText, linkStyle, linkTarget }, index) => (
                            <SnippetLink
                                key={`link-${index}`}
                                url={linkHref?.value}
                                className="snippet-video__link"
                                linkStyles={linkStyle?.value}
                                openInNewTab={linkTarget?.value}
                            >
                                {linkText?.value}
                            </SnippetLink>
                        ))}
                    </div>
                </div>

                <VideoComponent
                    snippetClassName="snippet-video"
                    url={video?.url.value ?? ''}
                    thumbnail={video?.thumbnail?.value}
                    thumbnailAlt={video?.alt?.value}
                    audioDescriptionUrl={video?.audioDescriptionUrl?.value}
                    onPlayClick={isModalMode ? () => setIsModalOpen(true) : undefined}
                />
            </div>

            {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions */}
            <dialog
                ref={dialogRef}
                className="snippet-video__modal"
                onClick={(e) => { if (e.target === e.currentTarget) closeModal() }}
            >
                <div className="snippet-video__modal-inner">
                    <button
                        className="snippet-video__modal-close-btn"
                        aria-label={`{{js.snippet.video.modal.close.aria-label}}`}
                        onClick={closeModal}
                    />
                    {isModalOpen && renderModalVideo()}
                </div>
            </dialog>
        </section>
    )
}

export default SnippetVideo
